import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { SITE, NAV } from "@/config/site";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn, { passive: true });
    fn();
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled || open ? "border-white/10 bg-joell-black/95 backdrop-blur-md" : "border-transparent bg-joell-black/70 backdrop-blur-sm"
      }`}
      data-testid="site-header"
    >
      <div className="container-x flex h-16 items-center justify-between gap-4 md:h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link" aria-label="JOELL home">
          <img src="/logo.png" alt="JOELL Asphalt Service & Maintenance Ltd." className="h-11 w-auto md:h-14" />
        </Link>

        <nav className="hidden items-center gap-5 lg:flex xl:gap-6" aria-label="Primary">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap font-mono text-[11px] font-bold uppercase tracking-[0.14em] transition-colors duration-200 ${
                location.pathname === item.to ? "text-joell-yellow" : "text-slate-300 hover:text-white"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <a href={SITE.phoneHref} className="hidden items-center gap-2 whitespace-nowrap font-mono text-xs font-bold tracking-widest text-slate-300 transition-colors hover:text-joell-yellow xl:flex" data-testid="header-phone-link">
            <Phone size={14} strokeWidth={2.5} />
            {SITE.phone} · {SITE.phone2}
          </a>
          <Link to="/request-quote" className="btn-accent hidden !px-5 !py-2.5 md:inline-flex" data-testid="nav-request-quote-button">
            Request a Quote
          </Link>
          <a href={SITE.phoneHref} className="btn-accent !px-4 !py-2.5 md:hidden" data-testid="header-mobile-call-button">
            <Phone size={14} strokeWidth={2.5} />
            Call
          </a>
          <button
            className="flex h-10 w-10 items-center justify-center border border-white/20 text-white transition-colors hover:border-joell-yellow hover:text-joell-yellow lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="header-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-joell-coal/100 backdrop-blur-md lg:hidden" data-testid="header-mobile-menu">
          <nav className="container-x flex flex-col py-4" aria-label="Mobile">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`border-b border-white/5 py-3.5 font-display text-xl font-semibold uppercase tracking-wide ${
                  location.pathname === item.to ? "text-joell-yellow" : "text-white"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="mt-5 grid grid-cols-2 gap-3 pb-2">
              <a href={SITE.phoneHref} className="btn-outline !px-3" data-testid="mobile-menu-call-button">Call {SITE.phone}</a>
              <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="btn-outline !px-3" data-testid="mobile-menu-whatsapp-button">WhatsApp</a>
              <Link to="/request-quote" className="btn-accent col-span-2" data-testid="mobile-menu-quote-button">Request a Free Quote</Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
